import React, {useState, useContext, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {graphql} from 'react-apollo';

import {AlertDanger} from '@homeaway/react-alerts';
import {FormTextAreaMinMax} from '@homeaway/react-form-components';
import {ButtonPrimary} from '@homeaway/react-buttons';
import ButtonLink from '@homeaway/react-buttons/lib/transpiled/ButtonLink';
import Tooltip from '@homeaway/react-tooltip';
import {SVGIcon} from '@homeaway/react-svg';
import {INFO__16} from '@homeaway/svg-defs/lib/defs/INFO__16';

import ModalHeading from '../ModalHeading/ModalHeading';
import ModifyBookingGuestPicker from '../../ModifyBookingGuestPicker/ModifyBookingGuestPicker';
import ModifyBookingAvailabilityCalendar from '../../ModifyBookingAvailabilityCalendar/ModifyBookingAvailabilityCalendar';
import {AvailabilityCalendarQuery} from '../../ModifyBookingAvailabilityCalendar/AvailabilityCalendarQuery';
import CancellationPolicyTooltip from './CancellationPolicyTooltip/CancellationPolicyTooltip';

import StayContext from '../../../../../context/StayContext';
import SystemContext from '../../../../../context/SystemContext';
import translate from '../../../../../../utils/translations';
import {
    getDaysFromCurrentDate,
    getLocalizedDate,
    SHORT_DATE_FORMAT,
    LONG_DATE_FORMAT,
} from '../../../../../../utils/dates';
import {
    MODIFY_BOOKING,
    MODIFY_REQUEST,
    modifyBookingCancellationPolicySelected,
    modifyBookingRequestDetailsPresented,
    modifyBookingRequestErrorPresented,
} from '../../../../../../utils/EDAP/ConversationDetails';

import './ModifyRequest.less';

const DEFAULT_MESSAGE_KEY = 'infoDefault';
const PRIOR_MESSAGE_KEY = 'infoPrior';
const THRESHOLD_DATE = 15;

const ModifyRequest = (props) => {
    const actionLocation = MODIFY_BOOKING;
    const experienceType = MODIFY_REQUEST;
    const {
        data,
        isErrorOccurred,
        policyType,
        setIsAvailabilityLoading,
        submitRequest,
        translations,
    } = props;
    const {
        stay: {
            booking: {
                arrival: existingCheckInDate,
                departure: existingCheckOutDate,
                guests: {
                    numberOfAdults: existingAdultsCount,
                    numberOfChildren: existingChildrenCount,
                },
                reservationUuid: reservationUUID,
            },
            unit: {
                integratedPropertyManager,
                ipmGuaranteedPricingActive,
                unitApiUrl,
            },
            ownerManaged,
        },
    } = useContext(StayContext);
    const {
        system: {fullLocale, isMobile},
    } = useContext(SystemContext);

    const [isPolicyTooltipOpen, setIsPolicyTooltipOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [updatedDates, setUpdatedDates] = useState({
        newCheckInDate: existingCheckInDate,
        newCheckOutDate: existingCheckOutDate,
    });
    const [updatedGuests, setUpdatedGuests] = useState({
        newAdultsCount: existingAdultsCount,
        newChildrenCount: existingChildrenCount,
    });
    const errorSection = useRef(null);

    const {newCheckInDate, newCheckOutDate} = updatedDates;
    const {newAdultsCount, newChildrenCount} = updatedGuests;

    const errorClassName = classNames({
        'margin-bottom-7x': isMobile,
        'margin-bottom-3x': !isMobile,
    });
    const infoClassName = classNames({
        'margin-bottom-7x': !isErrorOccurred && isMobile,
        'margin-bottom-3x': !isErrorOccurred && !isMobile,
    });

    const localizedCheckInDate = getLocalizedDate(
        existingCheckInDate,
        fullLocale,
        SHORT_DATE_FORMAT
    );
    const localizedCheckOutDate = getLocalizedDate(
        existingCheckOutDate,
        fullLocale,
        LONG_DATE_FORMAT
    );
    const isIpmNonGuaranteedPricing =
        (integratedPropertyManager && !ipmGuaranteedPricingActive) || false;
    const isTripComingSoon =
        getDaysFromCurrentDate(existingCheckInDate) <= THRESHOLD_DATE;

    useEffect(() => {
        modifyBookingRequestDetailsPresented({
            actionLocation,
            experienceType,
            reservationUUID,
        });
    }, [actionLocation, experienceType, reservationUUID]);

    useEffect(() => {
        if (isErrorOccurred) {
            modifyBookingRequestErrorPresented({
                actionLocation,
                experienceType: MODIFY_REQUEST,
                reservationUUID,
            });
            if (isMobile) {
                errorSection.current.scrollIntoView({behavior: 'smooth'});
            }
        }
    }, [isErrorOccurred, isMobile, actionLocation, reservationUUID]);

    const {error, getTravelerStay, loading, refetch} = data;

    function onOpenPolicyTooltip() {
        setIsPolicyTooltipOpen(true);
        modifyBookingCancellationPolicySelected({
            actionLocation,
            experienceType,
            reservationUUID,
        });
    }

    function onClosePolicyTooltip() {
        setIsPolicyTooltipOpen(false);
    }

    function onMessageChange(e) {
        setMessage(e.target.value);
    }

    const refetchData = () => {
        setIsAvailabilityLoading(true);
        refetch();
    };

    function tooltipContent() {
        return (
            <CancellationPolicyTooltip
                arrivalDate={newCheckInDate}
                isIpmNonGuaranteedPricing={isIpmNonGuaranteedPricing}
                policyType={policyType}
                translations={translations}
                unitUrl={unitApiUrl}
            />
        );
    }

    if (loading) {
        setIsAvailabilityLoading(true);
        return null;
    }

    const {listing, stayAvailability} = getTravelerStay?.booking || {};
    let {houseRules, minStayRange, sleeps} = listing || {};
    // Show error message when any field is coming as null or error occurs to avoid breaking the UI.
    // TODO: Wrap the component in error boundary with fallback UI and to report errors.
    const isDataNotAvailable =
        !!error || !stayAvailability || !minStayRange || !sleeps;

    if (isDataNotAvailable) {
        setIsAvailabilityLoading(false);
        const {networkError} = error || {};
        let errorMessage = 'modifyBooking.request.error.message.owner';
        if (!ownerManaged) {
            errorMessage =
                'modifyBooking.request.error.message.propertyManager';
        }
        let errorComponent = null;
        if (networkError) {
            errorComponent = (
                <div className="ModalRequest__error">
                    <AlertDanger
                        msg={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            errorMessage
                        )}
                        title={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.request.error.title'
                        )}
                    />
                    <div className="ModalRequest__error-button">
                        <ButtonPrimary
                            label={translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'th.button.try.again'
                            )}
                            onClick={() => refetchData()}
                        />
                    </div>
                </div>
            );
        } else {
            errorComponent = (
                <div className="ModalRequest__error">
                    <AlertDanger
                        msg={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            errorMessage
                        )}
                        title={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.request.error.title'
                        )}
                    />
                </div>
            );
        }
        return errorComponent;
    }

    setIsAvailabilityLoading(false);

    const isSubmitButtonDisabled =
        (message.trim().length === 0 &&
            existingCheckInDate === newCheckInDate &&
            existingCheckOutDate === newCheckOutDate &&
            existingAdultsCount === newAdultsCount &&
            existingChildrenCount === newChildrenCount) ||
        message.length > 1000;

    const sendRequest = () => {
        const updateBookingRequest = {bookingUuid: reservationUUID};
        if (
            existingCheckInDate !== newCheckInDate ||
            existingCheckOutDate !== newCheckOutDate
        ) {
            updateBookingRequest.newRequestedPeriodOfStay = {
                checkInDate: newCheckInDate,
                checkOutDate: newCheckOutDate,
            };
        }
        if (
            existingAdultsCount !== newAdultsCount ||
            existingChildrenCount !== newChildrenCount
        ) {
            updateBookingRequest.newRequestedGuests = {
                adults: `${newAdultsCount}`,
                children: `${newChildrenCount}`,
            };
        }
        const notes = message.trim();
        if (notes) {
            updateBookingRequest.notes = notes;
        }
        submitRequest(updateBookingRequest);
    };

    const guestCount = existingAdultsCount + existingChildrenCount;
    const guestCountText =
        guestCount === 1
            ? translate(
                  translations,
                  'trav-dash/trav-home',
                  'messages',
                  'th.inbox.conversation.tripSummary.guest'
              )
            : translate(
                  translations,
                  'trav-dash/trav-home',
                  'messages',
                  'th.inbox.conversation.tripSummary.guests',
                  guestCount
              );

    return (
        <div className="ModalRequest">
            <div className="ModalRequest__body">
                <ModalHeading>
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.title'
                    )}
                </ModalHeading>
                <p className="margin-bottom-4x">
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        `modifyBooking.request.${
                            isTripComingSoon
                                ? PRIOR_MESSAGE_KEY
                                : DEFAULT_MESSAGE_KEY
                        }`
                    )}
                </p>
                <h4 className="margin-bottom-1x">
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.existingBookingTitle'
                    )}
                </h4>
                <p className="ModalRequest__booking-text">
                    <span>{`${localizedCheckInDate} - ${localizedCheckOutDate}`}</span>
                    <span className="ModalRequest__booking-divider">{'|'}</span>
                    <span>{guestCountText}</span>
                </p>
                <h4>
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.dateGuestSelectionTitle'
                    )}
                </h4>
                <div className="ModalRequest__trip-details">
                    <ModifyBookingAvailabilityCalendar
                        checkInDate={newCheckInDate}
                        checkOutDate={newCheckOutDate}
                        minStayRange={minStayRange}
                        setUpdatedDates={setUpdatedDates}
                        stayAvailability={stayAvailability}
                        translations={translations}
                    />
                    <ModifyBookingGuestPicker
                        houseRules={houseRules}
                        setUpdatedGuests={setUpdatedGuests}
                        sleeps={sleeps}
                        translations={translations}
                    />
                </div>
                <div className="ModalRequest__policy">
                    <div className="ModalRequest__policy-icon">
                        <SVGIcon markup={INFO__16} inline useFill />
                    </div>
                    <div className="ModalRequest__policy-text">
                        <span className="ModalRequest__policy-text--main">
                            {translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'modifyBooking.request.policyText'
                            )}
                            &nbsp;
                        </span>
                        <Tooltip
                            content={tooltipContent()}
                            label="policy-popover"
                            isOpen={isPolicyTooltipOpen}
                            onClose={onClosePolicyTooltip}
                            placement="bottom"
                            tooltipType="popover"
                            trigger="none"
                            wrapperClassName="CancellationPolicyTooltip"
                        >
                            <ButtonLink
                                label={translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'modifyBooking.request.viewPolicyText'
                                )}
                                className="ModalRequest__policy-btn"
                                onClick={onOpenPolicyTooltip}
                            />
                        </Tooltip>
                    </div>
                </div>
                <h4>
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.messageTitle'
                    )}
                </h4>
                <FormTextAreaMinMax
                    id={'optional-text'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.messagePlaceholder'
                    )}
                    lingua={{
                        maxCharErrorMessage: translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.maxCharErrorMessage'
                        ),
                    }}
                    maxChars={1000}
                    onChange={onMessageChange}
                    placeholder={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.messagePlaceholder'
                    )}
                    rows={5}
                    srOnlyLabel
                    type={'text'}
                    value={message}
                />
                <p className={infoClassName}>
                    {
                        <b>
                            {translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'modifyBooking.request.alertBoldText'
                            )}
                        </b>
                    }
                    &nbsp;
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.request.alertText'
                    )}
                </p>
                {isErrorOccurred && (
                    <div ref={errorSection} className={errorClassName}>
                        <AlertDanger
                            msg={translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'modifyBooking.request.error.message'
                            )}
                            title={translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'modifyBooking.request.error.title'
                            )}
                        />
                    </div>
                )}
                <div className="ModalRequest__button-container">
                    <ButtonPrimary
                        disabled={isSubmitButtonDisabled}
                        label={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.request.submitRequestButton'
                        )}
                        onClick={sendRequest}
                    />
                </div>
            </div>
        </div>
    );
};

export default graphql(AvailabilityCalendarQuery, {
    options: (ownProps) => {
        return {
            variables: {
                conversationId: ownProps.conversationId,
            },
            errorPolicy: 'all',
        };
    },
})(ModifyRequest);

ModifyRequest.propTypes = {
    isErrorOccurred: PropTypes.bool,
    policyType: PropTypes.string,
    translations: PropTypes.shape({}),
    setIsAvailabilityLoading: PropTypes.func,
    submitRequest: PropTypes.func,
};
